import { Gender } from "./Gender";
import InsuranceInfoStatus from "../InsuranceInfoStatus";
import { FieldKey } from "../form/Field";
import sfetch from "../../utils/super-fetch";
import EntitySettingsId from "../EntitySettingsId";
import { dateWithOptionalTime } from "../../utils/date";

export default class PatientInfo {
  patientInformation: PatientInformation;
  attributes: AttributesForUser;
  tags: Tag[];
}

export class PatientManagementInfo extends PatientInfo {
  legalGuardians: PatientAndAccountInformation[];
}

export interface PatientAndAccountInformation extends PatientInformation {
  hasAccount: boolean;
  invitationSent: boolean;
}

export interface PatientInformation {
  contactInfoUid: string;
  externalUserId?: string;
  creation: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  preferredName?: string;
  birthDate?: string;
  email?: string;
  phoneNumbers: PhoneNumbers;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  gender?: Gender;
  socialSecurityNumber?: string;
  avatarHref: string;
  insuranceInfoStatus: InsuranceInfoStatus;
  isConvertedLead: boolean;
  acceptMarketingEmail: boolean;
}

export class PhoneNumbers {
  cellPhoneNumber?: string;
  homePhoneNumber?: string;
  workPhoneNumber?: string;
  primaryPhoneNumber?: string;
}

export class AttributesForUser {
  doctorUid: EntitySettingsId;
  doctorName?: string;
  practiceUid: EntitySettingsId;
  practiceName?: string;
  dsoUid: EntitySettingsId;
}

export class PatientPractice {
  practiceUid: string;
  practiceName: string;
}

export class PatientDoctor {
  doctorUid: string;
}

export class PatientLinks {
  patientPageLink?: string;
  patientPageEmailLink?: string;
  archiveUrl?: string;
  editTagsUrl?: string;
  reportSharingInfoLink: string;
}

export class Tag {
  uid: string;
  value: string;
  color: string;
}

export class TagActions {
  availableTagsUrl: string;
  leadTagsUrl: string;
  editTagsUrl?: string;
  createTagUrl: string;
  addTagToPatientUrl: string;
}

export class MarketingEmailOptions {
  displayConsent: boolean;
  isEditable: boolean;
  updateConsentPath: string;
  acceptMarketingEmail?: boolean;
}

export function findInformation(key: FieldKey, userInfo: PatientInformation) {
  switch (key) {
    case FieldKey.FIRST_NAME:
      return userInfo.firstName;
    case FieldKey.LAST_NAME:
      return userInfo.lastName;
    case FieldKey.EMAIL:
      return userInfo.email;
    case FieldKey.ZIP_CODE:
      return userInfo.zipCode;
    case FieldKey.CELL_PHONE_NUMBER:
      return userInfo.phoneNumbers.cellPhoneNumber;
    case FieldKey.WORK_PHONE_NUMBER:
      return userInfo.phoneNumbers.workPhoneNumber;
    case FieldKey.HOME_PHONE_NUMBER:
      return userInfo.phoneNumbers.homePhoneNumber;
    case FieldKey.BIRTH_DATE:
      return userInfo.birthDate ? dateWithOptionalTime(userInfo.birthDate) : null;
    case FieldKey.MIDDLE_NAME:
      return userInfo.middleName;
    case FieldKey.PREFERRED_NAME:
      return userInfo.preferredName;
    case FieldKey.GENDER:
      return userInfo.gender;
    case FieldKey.SSN:
      return userInfo.socialSecurityNumber;
    case FieldKey.ADDRESS:
      return userInfo.address;
    case FieldKey.CITY:
      return userInfo.city;
    case FieldKey.STATE:
      return userInfo.state;
    default:
      return null;
  }
}

export function invitePatientToCreateAccount(
  apiUrl: string,
  patientContactInfoUid: string,
  doctorUid: EntitySettingsId,
  legalGuardianContactInfoUid?: string
): Promise<any> {
  return sfetch(apiUrl, {
    method: "POST",
    body: JSON.stringify({
      patientContactInfoUid,
      doctorUid,
      legalGuardianContactInfoUid,
    }),
    headers: { "Content-Type": "application/json" },
  });
}
